/**
 * AuthGoogleCallback — Feb 2026
 *
 * Landing page for the direct-Google OAuth flow (customer-owned Google
 * Web Client). Google redirects the browser here after the user
 * authorizes with `?code=…&state=…`. We:
 *   1. Verify the `state` param against localStorage (CSRF).
 *   2. POST `{code, redirect_uri}` to `/api/auth/google/callback`.
 *   3. Store the returned session_token and route to /discover
 *      (or /profile/setup for a fresh sign-up).
 *
 * If the direct flow is not configured (`501` from the backend), we
 * bounce back to /auth so the user can retry via the Emergent Google
 * fallback. Any real error also lands them on /auth with a toast.
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { api, useAuth } from '@/contexts/AuthContext';

const STATE_KEY = 'google_direct_state';

export default function AuthGoogleCallback() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { login } = useAuth();
  const [status, setStatus] = useState('exchanging'); // exchanging | error
  const [errorMsg, setErrorMsg] = useState('');
  const consumed = useRef(false);

  useEffect(() => {
    if (consumed.current) return;
    consumed.current = true;

    const code  = params.get('code');
    const state = params.get('state');
    const err   = params.get('error');

    // Whichever URL Google redirected TO is the redirect_uri Google
    // expects to see in the token exchange — construct it identically.
    const redirectUri = window.location.origin + '/auth/google/callback';

    // Google reported an error (user denied, invalid scope, etc.)
    if (err) {
      setErrorMsg(err);
      setStatus('error');
      toast.error(`Google sign-in cancelled: ${err}`);
      setTimeout(() => navigate('/auth', { replace: true }), 1500);
      return;
    }
    if (!code) {
      setStatus('error');
      toast.error('Missing Google auth code');
      setTimeout(() => navigate('/auth', { replace: true }), 1500);
      return;
    }
    // CSRF check — state must match what we saved before redirecting.
    const savedState = sessionStorage.getItem(STATE_KEY);
    sessionStorage.removeItem(STATE_KEY);
    if (savedState && state && savedState !== state) {
      setStatus('error');
      toast.error('Session integrity check failed');
      setTimeout(() => navigate('/auth', { replace: true }), 1500);
      return;
    }

    (async () => {
      try {
        const res = await api.post('/auth/google/callback', {
          code,
          redirect_uri: redirectUri,
        });
        const data = res.data || {};
        if (!data.session_token) throw new Error('Session token missing');
        // Persist + hand to auth context; identical to /auth/google/session.
        login({
          user_id: data.user_id,
          email:   data.email,
          name:    data.name,
          picture: data.picture,
          profile_complete: data.profile_complete,
        }, data.session_token);
        navigate(data.profile_complete ? '/discover' : '/profile/setup', { replace: true });
      } catch (e) {
        // 501 = server-side env vars not configured → fall back to
        // Emergent Google flow so the user isn't stranded.
        if (e?.response?.status === 501) {
          toast.error('Direct Google auth not configured yet — using standard flow.');
          setTimeout(() => navigate('/auth', { replace: true }), 800);
          return;
        }
        const msg = e?.response?.data?.detail || 'Google sign-in failed';
        setErrorMsg(msg);
        setStatus('error');
        toast.error(msg);
        setTimeout(() => navigate('/auth', { replace: true }), 1500);
      }
    })();
  }, [params, navigate, login]);

  return (
    <div className="min-h-screen bg-[#FAFAFB] flex items-center justify-center px-4">
      <div className="rounded-2xl bg-white px-8 py-10 text-center max-w-sm w-full" style={{ border: '1px solid rgba(15,15,16,0.06)' }}>
        {status === 'exchanging' ? (
          <>
            <Loader2 size={26} className="animate-spin text-[#4285F4] mx-auto mb-4"/>
            <h1 className="font-[800] text-[18px] tracking-[-0.3px] mb-1" data-testid="auth-google-exchanging">
              Signing you in with Google…
            </h1>
            <p className="text-[13px] text-[#6B7280]">Just a moment.</p>
          </>
        ) : (
          <>
            <h1 className="font-[800] text-[18px] tracking-[-0.3px] mb-1 text-[#B91C1C]" data-testid="auth-google-error">
              Sign-in failed
            </h1>
            <p className="text-[13px] text-[#6B7280] break-words">{errorMsg || 'Please try again.'}</p>
          </>
        )}
      </div>
    </div>
  );
}
